import { useCallback, useState, useEffect, WheelEvent, RefObject } from 'react';

import { message } from 'antd';

import i18n from 'i18next';
import dayjs, { Dayjs } from 'dayjs';

import { AxiosResponse } from 'axios';

import redIcon from '@/assets/pictos/redWarning.svg';
import amberIcon from '@/assets/pictos/orangeWarning.svg';
import windDirection from '@/assets/pictos/windDirection.svg';
import windSpeed from '@/assets/pictos/windSpeed.svg';
import windGust from '@/assets/pictos/windGust.svg';
import temperature from '@/assets/pictos/temperature.svg';
import precipitation from '@/assets/pictos/precipitation.svg';

/// Environments Variables

// To get the Base URL name from the.env file
export const baseName = import.meta.env.BASE_URL;

// Check if current mode is Development
export const isDevMode = import.meta.env.DEV;

// Check if current mode is Regular
export const isRegularStart = import.meta.env.VITE_BYPASS_LOGIN === 'true';

//// Check if Path corresponds to Page
export function checkIfPathHasPage(currentPath: string, path: string) {
  const currentPathArray = currentPath.split('/');
  const pathArray = path.split('/').filter((item) => !item.includes(':'));
  return pathArray.every((item) => currentPathArray.includes(item));
}

/// To handle Response and Error

// i18n translate function with errorSuccessMessages namespace applied
const t = (text: string, data?: any): string =>
  i18n.t(text, { ns: 'errorSuccessMessages', ...data }) as string;

// Function to display success message from response

interface ResType {
  status: string;
  data: {
    errors: string | undefined;
    error?: string;
    err?: string;
    msg?: string;
    message?: string;
  };
}

export interface ResponseType {
  response: ResType;
}

export const getMessageFromResponse = (res: ResType | AxiosResponse) => {
  const responseMessage = res.data.msg || res.data.message;
  return responseMessage ? t(responseMessage, res.data) : undefined;
};

export const handleResponse = (
  res: ResType | AxiosResponse,
  msg?: string | Record<string, string>,
) => {
  const { status } = res;
  const responseMessage = getMessageFromResponse(res);
  if (typeof msg === 'string') {
    message.success(t(msg));
  } else if (typeof msg === 'object' && msg[status]) {
    message.success(t(msg[status]));
  } else if (responseMessage) {
    message.success(responseMessage);
  }
  return res;
};

// Functions to display error message from response

export const getMessageFromError = (err: ResponseType) => {
  const errorMessage =
    err.response.data.msg ||
    err.response.data.message ||
    err.response.data.err ||
    err.response.data.error ||
    (Array.isArray(err.response.data.errors)
      ? err.response.data.errors[0].message
      : undefined);
  return errorMessage || t(errorMessage as string, err.response.data);
};

export const handleError = (
  err: ResponseType,
  msg?: string | Record<string, string>,
) => {
  // get error status code
  const { status } = err.response;
  if (typeof msg === 'string') {
    message.error(t(msg), 5);
    return msg;
  }
  if (typeof msg === 'object' && msg[status]) {
    const messageToDisplay =
      typeof msg[status] === 'object' ? msg[status] : t(msg[status]);
    message.error(messageToDisplay, 5);
    return messageToDisplay;
  }
  const messageToDisplay = getMessageFromError(err);
  message.error(
    messageToDisplay || t('An error has occurred. Please try again.'),
    5,
  );
  throw new Error(messageToDisplay);
};

//// Data Processing functions

/**
 * Hook to debounce a value
 * @param value value to debounce
 * @param delay delay in milliseconds
 * @returns
 */
export const useDebounce = <T>(value: T, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timeout);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Debounce to differ State Variable change

export const useDebouncedEffect = (
  effect: () => any,
  dependencies: any[],
  delay: number,
) => {
  const callback = useCallback(effect, [effect, ...dependencies]);

  useEffect(() => {
    const handler = setTimeout(() => {
      callback();
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [callback, delay]);
};

/* Example of use case  */

// const [text, setText] = useState<string>('');
// const [debouncedText, setDebouncedText] = useState<string>('');

// useDebouncedEffect(
//   () => {
//     setDebouncedText(text);
//   },
//   [text],
//   1000,
// );

/**
 * Hook to get Screen Size and Height responsivly
 * @param debounce optional debounce time in milliseconds
 * @returns screen size
 */
export const useScreenSize = (debounce?: number) => {
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const debouncedScreenSize = useDebounce(screenSize, debounce);
  return debounce ? debouncedScreenSize : screenSize;
};

/**
 * Hook to get element bounding client rect
 * @param elementOrRef either a ref to the element or the element itself to observe
 * @returns bounding client rect with width and height
 */
export const useElementBoundingClientRect = (
  elementOrRef?: RefObject<HTMLElement | null> | HTMLElement | null,
) => {
  const [boundingClientRect, setBoundingClientRect] = useState<DOMRect>();
  useEffect(() => {
    const updateBoundingClientRect = () => {
      const element =
        elementOrRef && 'current' in elementOrRef
          ? elementOrRef.current
          : elementOrRef;
      if (element) {
        setBoundingClientRect(element.getBoundingClientRect());
      }
    };

    setTimeout(() => {
      updateBoundingClientRect();
    }, 0);

    window.addEventListener('resize', updateBoundingClientRect);
    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', updateBoundingClientRect);
    };
  }, [elementOrRef]);
  return boundingClientRect;
};

/**
 * Hook to get element size using ResizeObserver
 * @param elementOrRef either a ref to the element or the element itself to observe
 * @param debounceTimeout optional debounceTimeout time in milliseconds
 * @returns boundingRect with width and height
 */
export const useElementSize = (
  elementOrRef?: RefObject<HTMLElement | null> | HTMLElement | null,
  debounceTimeout?: number,
  borderBox = false,
) => {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element =
      elementOrRef && 'current' in elementOrRef
        ? elementOrRef.current
        : elementOrRef;

    if (!element) return undefined;

    let timeout: NodeJS.Timeout | null = null;

    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        let width: number;
        let height: number;

        if (borderBox) {
          width = entry.borderBoxSize[0].inlineSize;
          height = entry.borderBoxSize[0].blockSize;
        } else {
          width = entry.contentRect.width;
          height = entry.contentRect.height;
        }

        if (debounceTimeout && debounceTimeout > 0) {
          // Clear previous timeout
          if (timeout) {
            clearTimeout(timeout);
          }

          // Set new debounced timeout
          timeout = setTimeout(() => {
            setSize({ width, height });
          }, debounceTimeout);
        } else {
          // Immediate update
          setSize({ width, height });
        }
      });
    });

    resizeObserver.observe(element);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
      resizeObserver.disconnect();
    };
  }, [elementOrRef, debounceTimeout, borderBox]);

  return size;
};

// Function to Scroll Horizontally from a scrollable container Ref

export function onWheelHorizontalScroll(
  e: WheelEvent<HTMLDivElement>,
  ref: RefObject<HTMLDivElement | null>,
) {
  if (ref.current) {
    const containerScrollPosition = ref.current.scrollLeft;
    ref.current.scrollTo({
      top: e.deltaX,
      left: containerScrollPosition + e.deltaY,
    });
  }
}

/**
 * Round a number to two decimals
 * @param number {Number} the number to round
 * @returns rounded number with only 2 decimals
 * @link https://stackoverflow.com/questions/11832914/how-to-round-to-at-most-2-decimal-places-if-necessary
 */
export const roundNumberToDecimals = (
  number: number,
  decimals: number,
): number => Math.round((number + Number.EPSILON) * decimals) / decimals;

// Date Format functions

/**
 *
 * @param date Date value to process
 * @param locale from lang translation
 * @returns formatted human readable date
 */
export const humanDateFormat = (
  date: string | Dayjs,
  locale = 'en',
): string => {
  if (!date) return '---';
  return dayjs(date).locale(locale).format('DD MMMM YYYY');
};

/**
 * format date object to the backend expected format
 * @param date Date Object
 * @returns String representation of date param
 */
export const dateFormat = (date: Date) => dayjs(date).format('YYYY-MM-DD');

export const translateDateIntoIsoString = (date?: Dayjs) =>
  date ? date.format('YYYY-MM-DDTHH:mm:ss+00:00') : '';

/**
 * Generate all time steps between start and end with a configurable step duration (in hours)
 * @param stepHourDuration - Duration in hours between each step (e.g., 1 for hourly, 6 for 6-hourly)
 * @param start - Start date/datetime string (if date-only format YYYY-MM-DD, starts at 00:00:00)
 * @param end - End date/datetime string (if date-only format YYYY-MM-DD, extends to 23:59:59)
 * @param moreStepsToInclude - Number of additional steps to include after the end time (usually allowing to include the next day's 00:00 data)
 * @returns Array of ISO formatted datetime strings
 */
export const getHourlyStepsInRange = (
  stepHourDuration = 1,
  start?: string,
  end?: string,
  moreStepsToInclude = 0,
): string[] => {
  if (!start || !end) {
    return [];
  }

  // Helper to detect if a string is date-only format (YYYY-MM-DD)
  const isDateOnly = (dateString: string): boolean =>
    dayjs(dateString, 'YYYY-MM-DD', true).isValid();

  // Parse start time with dayjs.utc
  let startTime = dayjs.utc(start);
  if (!startTime.isValid()) {
    return [];
  }
  // Round down to the nearest hour
  startTime = startTime.startOf('hour');

  // Parse end time with dayjs.utc
  let endTime = dayjs.utc(end);
  if (!endTime.isValid()) {
    return [];
  }

  // If date-only format, extend to end of day; otherwise use exact time and round to hour
  if (isDateOnly(end)) {
    endTime = endTime.endOf('day');
  } else {
    endTime = endTime.startOf('hour');
  }

  // Ensure endTime is after startTime
  if (endTime.isBefore(startTime)) {
    return [];
  }

  // Generate steps
  const steps: string[] = [];
  let current = startTime;

  while (current.isBefore(endTime) || current.isSame(endTime)) {
    steps.push(current.format('YYYY-MM-DDTHH:mm:ss+0000')); // @TODO: use translateDateIntoIsoString instead when backend is updated (+0000 -> +00:00)
    current = current.add(stepHourDuration, 'hour');
  }
  for (let i = 0; i < moreStepsToInclude; i += 1) {
    steps.push(current.format('YYYY-MM-DDTHH:mm:ss+0000')); // @TODO: use translateDateIntoIsoString instead when backend is updated (+0000 -> +00:00)
    current = current.add(stepHourDuration, 'hour');
  }

  return steps;
};

/**
 * format date object to the backend expected format in an URL: YYYYMMDDTHHmmss
 * @param date Date Object
 * @returns String representation of date param
 */
export const formatDateToUrlHorodatetime = (date: string | Dayjs) =>
  dayjs.utc(date).format('YYYYMMDDTHHmmss');

/**
 * @param month
 * @param year
 * @returns Formatted human friendly semester dates
 */
const semester = (month: number, year: Date) => {
  if (month < 6) {
    return `S1 ${dayjs(year).format('YYYY')}`;
  }
  return `S2 ${dayjs(year).format('YYYY')}`;
};

/**
 * Returns named categories depending on selected frequency and date
 * @param date date value
 * @param frequency week | month | quarter | semester
 * @returns formatted human friendly date value
 */
export const selectDataFrequency = (frequency: string, date: Date) => {
  switch (frequency) {
    case 'week':
      return `W${dayjs(date).week()} ${dayjs(date).format('YYYY')}`;
    case 'month':
      return dayjs(date).format('MMM YYYY');
    case 'quarter':
      return `Q${dayjs(date).quarter()} ${dayjs(date).format('YYYY')}`;
    case 'semester':
      return semester(dayjs(date).month(), date);
    default:
      return date;
  }
};

export function getFaultsColorByValue(
  faultCount: number,
  currentFaultBand: IFaultBand,
): string {
  const { firstLowerBand, secondLowerBand } = currentFaultBand;
  let color = '';

  if (faultCount <= firstLowerBand) {
    color = '#3bb51d';
  } else if (faultCount > firstLowerBand && faultCount <= secondLowerBand) {
    color = '#ee8716';
  } else if (faultCount > secondLowerBand) {
    color = '#de1313';
  }
  return color;
}

export function getFaultsColor(color: string): string {
  switch (color) {
    case 'label.green':
      return '#3bb51d';
    case 'label.yellow':
      return '#ee8716';
    case 'label.red':
      return '#de1313';
    default:
      return '';
  }
}

export const getWarningIcon = (data: ILicenceAreaDistrictDaily[]) => {
  const faultBands = (Array.isArray(data) ? data : [data])
    .flatMap((item) => item.forecasts as IForecast[])
    .map((f) => f.faultBand24h)
    .filter((fb): fb is number => typeof fb === 'number')
    .filter((fb) => Number.isFinite(fb));
  const higherFaultBand = Math.max(...faultBands);
  let warning = '';
  if (higherFaultBand === 3) {
    warning = redIcon;
  } else if (higherFaultBand === 2) {
    warning = amberIcon;
  }
  return warning;
};

export const getWindDirection = (angle: number) => {
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return directions[Math.abs(Math.round(angle / 45) % 8)] || '';
};

export const getUnit = (name: string) => {
  switch (name) {
    case 'confidence':
      return '%';
    case 'averageWindDirection':
    case 'windDirection':
      return '°';
    case 'maxWindSpeed':
    case 'windSpeed':
      return 'mph';
    case 'maxWindGust':
    case 'windGust':
      return 'mph';
    case 'averageTemperature':
    case 'temperature':
      return '°C';
    case 'totalPrecipitation':
    case 'precipitation':
      return 'mm';
    default:
      return '';
  }
};
export const getType = (name: string) => {
  switch (name) {
    case 'averageWindDirection':
      return 'windDirection';
    case 'maxWindSpeed':
      return 'windSpeed';
    case 'maxWindGust':
      return 'windGust';
    case 'averageTemperature':
      return 'temperature';
    case 'totalPrecipitation':
      return 'precipitation';
    default:
      return '';
  }
};

export const getSubTitle = (name: string) => {
  switch (name) {
    case 'faultBand':
      return '6-hourly';
    case 'faultBand24h':
      return 'daily';
    case 'averageWindDirection':
      return 'avg.';
    case 'maxWindSpeed':
      return 'max.';
    case 'maxWindGust':
      return 'max.';
    case 'averageTemperature':
      return 'avg.';
    case 'totalPrecipitation':
      return 'cuml.';
    default:
      return '';
  }
};

export const getIcon = (name: string) => {
  switch (name) {
    case 'averageWindDirection':
    case 'windDirection':
      return windDirection;
    case 'maxWindSpeed':
    case 'windSpeed':
      return windSpeed;
    case 'maxWindGust':
    case 'windGust':
      return windGust;
    case 'averageTemperature':
    case 'temperature':
      return temperature;
    case 'totalPrecipitation':
    case 'precipitation':
      return precipitation;
    default:
      return '';
  }
};

export function getStartOfTimeBand(time: Dayjs | undefined) {
  if (!time) {
    return dayjs();
  }
  if (time.hour() > 0 && time.hour() < 6) {
    return time.set('hour', 0).set('minute', 0).set('second', 0);
  }
  if (time.hour() >= 6 && time.hour() < 12) {
    return time.set('hour', 6).set('minute', 0).set('second', 0);
  }
  if (time.hour() >= 12 && time.hour() < 18) {
    return time.set('hour', 12).set('minute', 0).set('second', 0);
  }
  if (time.hour() >= 18 && time.hour() <= 23) {
    return time.set('hour', 18).set('minute', 0).set('second', 0);
  }
  return time.set('hour', 0).set('minute', 0).set('second', 0);
}

export function getFaultContent(
  level: number,
  firstLowerBand: number,
  secondLowerBand: number,
  licenceAreaImpact?: boolean,
) {
  if (licenceAreaImpact) {
    return `F= ${level || 0}`;
  }
  switch (level) {
    case 1:
      return `F ≤ ${firstLowerBand}`;
    case 2:
      return ` ${firstLowerBand} < F ≤ ${secondLowerBand} `;
    case 3:
      return `F > ${secondLowerBand}`;
    default:
      return `F > ${secondLowerBand}`;
  }
}

export function getWarningLevel(level?: number) {
  switch (level) {
    case 3:
      return '#CC0033';
    case 2:
      return '#FE9900';
    case 1:
      return '#FFE922';
    default:
      return null;
  }
}

export function getFaultColor(level: number) {
  switch (level) {
    case 3:
      return '#de1314';
    case 2:
      return '#ee8716';
    case 1:
      return '#3cb51c';
    default:
      return '#3cb51c';
  }
}

export function getFaultColorHover(color: number) {
  switch (color) {
    case 3:
      return '#e35554';
    case 2:
      return '#efa757';
    case 1:
      return '#71c75c';
    default:
      return '#71c75c';
  }
}

export function setLevelColor(status: number) {
  switch (status) {
    case 1:
      return '#FAE55B';
    case 2:
      return '#EE8E40';
    case 3:
      return '#BF2828 ';
    default:
      return '#FAE55B';
  }
}

export const getWarningLevelText = (level: number | string) => {
  switch (level) {
    case 1:
      return 'Yellow';
    case 2:
      return 'Amber';
    case 3:
      return 'Red';
    case 'none':
      return null;
    default:
      return null;
  }
};

// Color mapping for different substation types
export const getSubstationColor = (type: SubstationType): string => {
  switch (type) {
    case 'HV':
      return '#40136c';
    case '33kV':
      return '#a03098';
    case '132kV':
      return '#ff4dc3';
    default:
      return '#8c8c8c';
  }
};

/**
 * Utility function to get typedentries of a record
 * @param obj Record to get entries from
 * @returns Entries of the record
 */
export function entriesOf<K extends string, V>(obj: Record<K, V>): [K, V][] {
  return Object.entries(obj) as [K, V][];
}
